import { Skeleton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";

import type { SalesAgentControl, SalesAgentMode } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";

/**
 * "Which agent works" selector — labelled with the AGENT names so it reads the
 * same as the rest of the section (the AgentWorkingNow strip, the Status panel,
 * the Harvester tab). Sits in the Sales header next to Pause/Resume.
 *
 * - "Lead Generator" → mode `leads` (the signal-driven lead agent, the default).
 * - "Harvester"      → mode `contacts` (the blind contact-harvester).
 * - "Both"           → mode `both`.
 * - The two share one budget + one pause button; this picks who's working.
 * - Hidden for non-editors (no `sales:edit`) — they still see who's working via
 *   the AgentWorkingNow strip + the Status panel.
 *
 * Reuses the `["sales","agent-control"]` query the Pause button already polls,
 * so the toggle and the pause control always agree.
 */
export function AgentModeToggle(): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();

  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const setMode = useMutation({
    mutationFn: (mode: SalesAgentMode) => salesApi.setAgentMode(mode),
    onSuccess: (state: SalesAgentControl) => {
      qc.setQueryData(["sales", "agent-control"], state);
      // Mode changes which track works → refresh the health + status surfaces.
      qc.invalidateQueries({ queryKey: ["sales", "health"] });
      qc.invalidateQueries({ queryKey: ["sales", "today"] });
    },
  });

  if (!canEdit) return null;
  if (!ctl.data) return <Skeleton variant="rounded" width={172} height={32} />;

  const mode = ctl.data.mode ?? "leads";

  return (
    <Tooltip title="Which agent works: the Lead Generator, the Harvester, or both. They share one budget.">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        disabled={setMode.isPending}
        onChange={(_, next: SalesAgentMode | null) => {
          if (next && next !== mode) setMode.mutate(next);
        }}
        aria-label="which agent works"
      >
        <ToggleButton value="leads" aria-label="lead generator">Lead Generator</ToggleButton>
        <ToggleButton value="contacts" aria-label="harvester">Harvester</ToggleButton>
        <ToggleButton value="both" aria-label="both agents">Both</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
}
